'use strict';

define('vb/private/events/aiEventBus',['vb/private/constants'], (Constants) => {
  /**
   * @typedef {import('vb/private/stateManagement/container.js').default} Container
   */

  /**
   * The AiEventBus class provides a centralized event handling system for AI-related events.
   *
   * @class AiEventBus
   */
  class AiEventBus {
    /**
     * Initializes a new instance of the AiEventBus class.
     *
     * This constructor sets up two empty objects to store event listeners:
     * - aiResponseEventListeners: stores event listeners for AI response events
     * - aiRequestEventListeners: stores event listeners for AI request events
     *
     * @memberof AiEventBus
     */
    constructor() {
      this.aiResponseEventListeners = {};
      this.aiRequestEventListeners = {};
    }

    /**
     * Adds event listeners for AI response events.
     *
     * @param {object} eventListenerDefs - An object containing event listener definitions.
     * @param {Container} container - The container associated with the event listeners.
     * @returns {void}
     */
    addAiResponseEventListeners(eventListenerDefs, container) {
      AiEventBus.addEventListener(this.aiResponseEventListeners, eventListenerDefs, container);
    }

    /**
     * Removes event listeners for AI response events associated with the specified container.
     *
     * @param {Container} container - The container whose event listeners will be removed.
     * @returns {void}
     */
    removeAiResponseEventListeners(container) {
      AiEventBus.removeEventListener(this.aiResponseEventListeners, container);
    }

    /**
     * Adds event listeners for AI request events.
     *
     * @param {object} eventListenerDefs - An object containing event listener definitions.
     * @param {Container} container - The container associated with the event listeners.
     * @returns {void}
     */
    addAiRequestEventListeners(eventListenerDefs, container) {
      AiEventBus.addEventListener(this.aiRequestEventListeners, eventListenerDefs, container);
    }

    /**
     * Removes event listeners for AI request events associated with the specified container.
     *
     * @param {Container} container - The container whose event listeners will be removed.
     * @returns {void}
     */
    removeAiRequestEventListener(container) {
      AiEventBus.removeEventListener(this.aiRequestEventListeners, container);
    }

    /**
     * Registers an event listener with the event bus.
     *
     * @param {object} eventListeners - A dictionary of existing event listeners, keyed by full path.
     * @param {object} eventListenerDefs - An object containing event listener definitions, where each key is an event
     *                                     name and each value is a function to handle that event.
     * @param {Container} container - The container associated with the event listeners, which must have a `fullPath`
     *                             property.
     * @throws {Error} If the `container` does not have a `fullPath` property.
     * @returns {void}
     */
    static addEventListener(eventListeners, eventListenerDefs, container) {
      if (Object.keys(eventListenerDefs).length > 0) {
        // TODO: handle multiple instances of the same fragment
        const { fullPath } = container;

        // eslint-disable-next-line no-param-reassign
        eventListeners[fullPath] = {
          eventListenerDefs,
          container,
        };
      }
    }

    /**
     * Removes an event listener from the event bus.
     *
     * @param {object} eventListeners - A dictionary of existing event listeners, keyed by full path.
     * @param {Container} container - The container associated with the event listener to be removed. It must have a
     *                            `fullPath` property.
     * @throws {TypeError} If either `eventListeners` or `container` is null or undefined.
     * @returns {void}
     */
    static removeEventListener(eventListeners, container) {
      // TODO: handle multiple instances of the same fragment
      const { fullPath } = container;

      // eslint-disable-next-line no-param-reassign
      delete eventListeners[fullPath];
    }

    /**
     * Fires an AI response event to notify registered listeners.
     *
     * @async
     * @param {*} payload - The data payload associated with the event.
     * @param {string} eventName - The name of the event being fired.
     * @param {string} [target] - Optional target container to filter event listeners.
     * @returns {Promise<*>} A promise resolving to an array of settled results from the event listeners.
     */
    async fireAiResponseEvent(payload, eventName, target) {
      const eventListeners = AiEventBus
        .lookupEventListeners(this.aiResponseEventListeners, eventName, target);

      return AiEventBus.fireEvent(eventListeners, payload);
    }

    /**
     * Fires an AI request event to notify registered listeners.
     *
     * @async
     * @param {*} payload - The data payload associated with the event.
     * @param {string} [eventName='default'] - The optional name of the event being fired. Defaults to 'default'.
     * @param {Container[]} [targetsToSkip=[]] an array of containers to skip sending the event.
     * @returns {Promise<*>} A promise resolving to an array of settled results from the event listeners.
     */
    async fireAiRequestEvent(payload, eventName = 'default', targetsToSkip = []) {
      const eventListeners = AiEventBus
        .lookupEventListeners(this.aiRequestEventListeners, eventName);

      return AiEventBus.fireEvent(eventListeners, payload, targetsToSkip);
    }

    /**
     * Fires an AI plugin mounted event to notify registered listeners only if the container itself has
     * aiResponseEventListeners registered.
     *
     * @async
     * @param {Container} container - The container associated with the event listener.
     * @returns {Promise<*>} A promise resolving to the result of firing the AI plugin mounted event, or undefined
     *                      if no event listeners are registered.
     */
    async fireAiPluginMountedEvent(container) {
      const { fullPath, extensionId } = container;
      const eventListener = this.aiResponseEventListeners[fullPath];

      if (eventListener) {
        const { eventListenerDefs } = eventListener;
        const payload = {
          id: fullPath,
          extensionId,
          events: Object.keys(eventListenerDefs),
        };

        // The vbAiPluginMounted event should not be sent to the container that is being mounted so add it to the
        // targetsToSkip list.
        return this.fireAiRequestEvent(payload, Constants.AI_PLUGIN_MOUNTED_EVENT, [container]);
      }

      return undefined;
    }

    /**
     * Looks up event listener definitions in the provided event listeners dictionary.
     *
     * If a target is specified, it searches for an event listener definition matching the given event name within the
     * target's event listeners. Otherwise, it searches for all event listener definitions matching the given event
     * name across all event listeners.
     *
     * @param {object} eventListeners - A dictionary of event listeners, keyed by full path.
     * @param {string} eventName - The name of the event to search for.
     * @param {string} [target] - Optional target container to filter event listeners.
     * @returns {Array<{ eventListenerDef: object, container: Container }>} An array of event listener definitions
     *                                                                     matching the given event name.
     */
    static lookupEventListeners(eventListeners, eventName, target) {
      const listenerDefs = [];

      if (target) {
        const eventListener = eventListeners[target];
        if (eventListener) {
          const eventListenerDef = this.lookupEventListener(eventListener, eventName);
          if (eventListenerDef) {
            listenerDefs.push(eventListenerDef);
          }
        }
      } else {
        // if no target is provided, look for all event listener matching the eventName
        Object.values(eventListeners).forEach((eventListener) => {
          const eventListenerDef = this.lookupEventListener(eventListener, eventName);
          if (eventListenerDef) {
            listenerDefs.push(eventListenerDef);
          }
        });
      }

      return listenerDefs;
    }

    /**
     * Retrieves an event listener definition from the provided event listener object.
     *
     * @param {object} eventListener - The event listener object to retrieve the definition from.
     * @param {string} eventName - The name of the event to retrieve the listener definition for.
     * @returns {{ eventListenerDef: object, container: Container } | null} The event listener definition and its
     *                                                        associated container, or null if no definition is found.
     */
    static lookupEventListener(eventListener, eventName) {
      const {
        eventListenerDefs,
        container,
      } = eventListener;

      const eventListenerDef = eventListenerDefs[eventName];
      return eventListenerDef ? { eventListenerDef, container } : null;
    }

    /**
     * Fires an event to notify registered listeners.
     *
     * @async
     * @param {Array<{ eventListenerDef: object, container: Container }>} eventListeners - An array of event listener
     *                                                                                     definitions.
     * @param {*} payload - The data payload associated with the event.
     * @param {Container[]} [targetsToSkip=[]] an array of containers to skip sending the event.
     * @returns {Promise<any[]>} A promise resolving to an array of settled results from the event listeners.
     */
    static async fireEvent(eventListeners, payload, targetsToSkip = []) {
      const promises = [];

      eventListeners.forEach(({ eventListenerDef, container }) => {
        if (!targetsToSkip.includes(container)) {
          const event = {
            payload,
          };

          const availableContext = container.getAvailableContexts()
            .clone();
          availableContext[Constants.ContextName.EVENT] = event;

          const chainFunctionWrappers = container.createListenerChainFunctions(eventListenerDef);
          promises.push(container.callListenerChainFunctionsInParallel(chainFunctionWrappers, availableContext));
        }
      });

      return Promise.allSettled(promises);
    }
  }

  return AiEventBus;
});

